/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */
// Sticky ATC
function initStickyATC() {
    var atcOffset = $('.lp-atc').offset();
	var stickyAtc = $('#lp-sticky-atc');
  
    stickyAtc.hide();
  
  if(atcOffset) {
    $(window).scroll(function() {
        if ($(window).scrollTop() > atcOffset.top) {
            stickyAtc.show();
        } else {
            stickyAtc.hide();
        }
    })
  }
}



//PDP timer progress bar 
function calculateProgress(currentDaysLeft) {
    var days = $('.countdown-progress').data('days-bar');
    var half = days / 2;
    var quarter = days / 4;
    var third = quarter * 3;
    var eighth = days / 8;
    var quarterPlusEighth = quarter + eighth;
    var halfPlusEighth = half + eighth;
    var thirdPlusEighth = third + eighth;
    var innitialDaysLeft = days;
    var daysDiff = days - currentDaysLeft;
   
  if (daysDiff >= days) {
        $('.fill-progress').css('max-width', '100%');
    } else if (daysDiff > thirdPlusEighth) {
        $('.fill-progress').css('max-width', '90%');
    } else if (daysDiff > third) {
        $('.fill-progress').css('max-width', '75%');
    } else if (daysDiff > halfPlusEighth) {
        $('.fill-progress').css('max-width', '65%');
    } else if (daysDiff > half) {
        $('.fill-progress').css('max-width', '50%');
    } else if (daysDiff > quarterPlusEighth) {
        $('.fill-progress').css('max-width', '35%');
    } else if (daysDiff > quarter) {
        $('.fill-progress').css('max-width', '25%');
    } else if (daysDiff > eighth) {
        $('.fill-progress').css('max-width', '15%');
    } else if (daysDiff <= eighth) {
        $('.fill-progress').css('max-width', '5%');
    }

};


//PDP timer 
function initTimer() {
  var year = $('.countdown-wrapper #timer').data('year');
  var month = $('.countdown-wrapper #timer').data('month');
  var day = $('.countdown-wrapper #timer').data('day');
  var hours = $('.countdown-wrapper #timer').data('hours');
  var minutes = $('.countdown-wrapper #timer').data('minutes');
  var seconds = $('.countdown-wrapper #timer').data('seconds');
  
  future  = Date.parse(month + ' ' + day  + ',' + ' ' + year + ' ' + hours + ':'  + minutes + ':' + seconds);
  
  now     = new Date();
  diff    = future - now;

  days  = Math.floor( diff / (1000*60*60*24) );
  hours = Math.floor( diff / (1000*60*60) );
  mins  = Math.floor( diff / (1000*60) );
  secs  = Math.floor( diff / 1000 );
  
  d = days;
  h = hours - days  * 24;
  m = mins  - hours * 60;
  s = secs  - mins  * 60;
  
  if(d < 1) {
    d = 0
  }
  
  document.getElementById("timer")
    .innerHTML =
      '<div class="countdown-days">' + d + '</div>' +
      '<div class="countdown-hours">' + h + '<span>hours</span></div>' +
      '<div class="countdown-minutes">' + m + '<span>minutes</span></div>' +
      '<div class="countdown-seconds">' + s + '<span>seconds</span></div>' ;
  calculateProgress(days);
  initCustomersCounter(days); 
}


//stock counter 
function initCounter() {
    var stockNum1 = $('.lp-stock-number').first();
    var stockNum2 = $('.lp-stock-number').eq(1);
    var stockNum3 = $('.lp-stock-number').eq(2);
    var stockNum4 = $('.lp-stock-number').eq(3);
    var stockNum5 = $('.lp-stock-number').eq(4);
    var stockNum1Text = $('.lp-stock-number').first().text();
    var stockNum2Text = $('.lp-stock-number').eq(1).text();
    var stockNum3Text = $('.lp-stock-number').eq(2).text();
    var stockNum4Text = $('.lp-stock-number').eq(3).text();
    var stockNum5Text = $('.lp-stock-number').eq(4).text();

    var intMinutes = 10;
    var now = new Date();
    var nowMLSeconds = now.getTime();
    var addMlSeconds = (intMinutes * 60) * 1000;
    var newDateObj = new Date(nowMLSeconds + addMlSeconds);
    var newDateObjMlSeconds = newDateObj.getTime();
  
    //set the target time in local storage
   if(!localStorage.getItem('future-time')){
   	  localStorage.setItem('future-time', newDateObjMlSeconds);
   } 
   
    var localDate = localStorage.getItem('future-time');

  //check if current time == target time
    if(nowMLSeconds > localDate) {    // this needs to be ">"

      var numToSubstract1 = Math.floor(Math.random() * 3) + 1;
      var numToSubstract2 = Math.floor(Math.random() * 3) + 1;
      var numToSubstract3 = Math.floor(Math.random() * 3) + 1;
      var numToSubstract4 = Math.floor(Math.random() * 3) + 1;
      var numToSubstract5 = Math.floor(Math.random() * 3) + 1;
      
      stockNum1Text -= numToSubstract1;
      if(stockNum1Text < 1){stockNum1Text = 10};
      stockNum2Text -= numToSubstract2;
      if(stockNum2Text < 1){stockNum2Text = 10};
      stockNum3Text -= numToSubstract3;
      if(stockNum3Text < 1){stockNum3Text = 10};
      stockNum4Text -= numToSubstract4;
      if(stockNum4Text < 1){stockNum4Text = 10};
      stockNum5Text -= numToSubstract5;
      if(stockNum5Text < 1){stockNum5Text = 10};
      
//       var numsArr = [stockNum1Text,stockNum2Text,stockNum3Text,stockNum4Text,stockNum5Text];
//       var newNumsArr = numsArr.map(biggerThanZero);
//       console.log(numsArr);
//       function biggerThanZero(num) {
//         if(num < 1) {
//  		 num = 10;
//         }
//         return num;
// 	  }
      
//       console.log(newNumsArr);
      
      $(stockNum1).text(stockNum1Text); 
      $(stockNum2).text(stockNum2Text); 
      $(stockNum3).text(stockNum3Text);
      $(stockNum4).text(stockNum4Text); 
      $(stockNum5).text(stockNum5Text); 
      
      localStorage.setItem('future-time', newDateObjMlSeconds);     
  }

}



//open email form
$('.display-email-form').click(function(){
	$('#landing-page .section-footer__row__col.section-footer__newsletter-block').toggle('medium');
})


//brand referral slider

// function initLpSlider() {
//   if ( $( "#lp-carousel-wrapper .lp-carousel" ).length ) {
//     $( ".lp-carousel").slick( {
//       slidesToShow: 5,
//       slidesToScroll: 1,

//       responsive: [
//    		 {
//      	   breakpoint: 678,
//            settings: {
//       		  slidesToShow: 3,
//       		  slidesToScroll: 1,
//       		  infinite: true
//       		}
//          }
//       ]
//     });
//   }
// }


//build href for atc in popup
// function buildAtcHref(ids, popupAtc) {
//   var idsArr = [];

//   ids.each(function () {
//     idsArr.push($(this).data('id'));
//   });

//   // Update the atcHref when the quantity input changes
//   function updateAtcHref() {
//     var atcHref = '/cart/'; // Reset the atcHref to start fresh
//     var quantity = $('#quantity').val();
//     idsArr.forEach(id => atcHref += id + ':' + quantity + ',');
//     popupAtc.attr('href', atcHref);
//   }

//   // Call the updateAtcHref function on page load
//   updateAtcHref();

//   // Add event listeners to the plus and minus buttons
//   $('.quantity-btn').on('click', function (event) {
//     event.preventDefault();
//     var direction = $(this).data('quantity');
//     var quantityInput = $('#quantity-{{ product_id }}');
//     var currentQuantity = parseInt(quantityInput.val());

//     if (direction === 'up') {
//       quantityInput.val(currentQuantity + 1);
//     } else if (direction === 'down' && currentQuantity > 1) {
//       quantityInput.val(currentQuantity - 1);
//     }

//     updateAtcHref();
//   });

//   // Add an event listener to the quantity input to detect manual changes
//   $('#quantity').on('input', updateAtcHref);
// }
                   
// $('.popup-open').on('click', function() {
//   var $productSticky = $(this).closest(".product-sticky");
//   var popupOpenId = $productSticky.find(".popup-open").attr("id");
//   var popupOpenIndex = popupOpenId.substr(-1);
//   var popupBkgToOpen = $productSticky.find(`.popup-bkg${popupOpenIndex}`);
//   var popupAtc = $productSticky.find(`.popup-bkg${popupOpenIndex} #popup-atc`);
//   var ids = $productSticky.find(`.popup-bkg${popupOpenIndex} .popup-product option:selected`);
  
//   $(".cc-animate-enabled .shopify-section").css("position", "relative");
//   $(".bg-overlay").css("display", "block");

//  $(".quantity_up").on("click", function () {
//     var $this = $(this);
//     var qplus = parseInt($this.prev().val()) + 1;
//     var $quantityWrapper = $this.closest(".quantity-wrapper");

//     $quantityWrapper.find(".variant_quantity").attr("data-quantity", qplus);
//     $quantityWrapper.find(".var_qut").attr("data-quantity", qplus);

//     var $popupBkg = $this.closest(".popup-select-options");
//     var valunew = $popupBkg.find(".custom-checkout a").attr("href");
//     var ahrefvalue = valunew.split(":");
//     var resultas = ahrefvalue[0];

//     $popupBkg.find(".custom-checkout a").attr(
//       "href",
//       resultas + ":" + qplus
//     );
// });

//  $(".quantity_down").on("click", function () {
//     var $this = $(this);
//     var qminus = parseInt($this.next().val()) - 1;
//     if (qminus >= 1) {
//         var $quantityWrapper = $this.closest(".quantity-wrapper");
//         var $popupBkg = $this.closest(".popup-select-options");

//         $quantityWrapper.find(".variant_quantity").attr("data-quantity", qminus);
//         $quantityWrapper.find(".var_qut").attr("data-quantity", qminus);

//         var valunew = $popupBkg.find(".custom-checkout a").attr("href");
//         var ahrefvalue = valunew.split(":");
//         var resultas = ahrefvalue[0];

//         $popupBkg.find(".custom-checkout a").attr(
//             "href",
//             resultas + ":" + qminus
//         );
//     }
//     // else{
//     //   $('.variant_quantity').attr('data-quantity',1);
//     //   $('.var_qut').attr('data-quantity',1);
//     //    ids = $(`.popup-bkg${popupOpenIndex} .popup-product input.opt-btn:checked`);
//     //   buildAtcHref(ids, popupAtc);
//     // }
//   });

// $('.update_quantity').on('click', function() {
//     var newQuantity = parseInt($(this).siblings('.quantity').val());
    
//     // Update your data-quantity attributes or perform other actions as needed
//     $('.variant_quantity').attr('data-quantity', newQuantity);
//     $('.var_qut').attr('data-quantity', newQuantity);

//     var parentElement = $(this).parent().parent().parent().parent();
//     var parentClass = parentElement.attr("id");

//     // Check if the element with ID exists
//     if ($("#" + parentClass + " .custom-checkout a").length > 0) {
//         var valunew = $("#" + parentClass + " .custom-checkout a").attr("href");

//         // Check if href attribute is defined
//         if (valunew !== undefined) {
//             var ahrefvalue = valunew.split(":");
//             var resultas = ahrefvalue[0];
//             $("#" + parentClass + " .custom-checkout a").attr("href", resultas + ":" + newQuantity);
//         }
//     }
// });

// // Trigger click on .update_quantity when hovering over .popup-atc
// $('.popup-atc').hover(function() {
//   // console.log('test')
//     $('.update_quantity').click();
// });




  
//     //call function on popup open
//     buildAtcHref(ids, popupAtc);
  
//     //call function when variant is changed
//     $('.popup-bkg .popup-product select').on('change', function() {
//         ids = $(`.popup-bkg${popupOpenIndex} .popup-product option:selected`);
//         buildAtcHref(ids, popupAtc);
//     });
// $('.opt-btn').on('click', function() {
//  ids = $(`.popup-bkg${popupOpenIndex} .popup-product input.opt-btn:checked`);
//         buildAtcHref(ids, popupAtc);
//   // alert('abc');
// });
//     //open popup
//     popupBkgToOpen.fadeIn(500, function() {
//         popupBkgToOpen.addClass('active-flex');
//     });
//     //prevent scroll when popup is opened
//     $('body').addClass('block-scroll');
  
//     //fix overlapping on SAFARI
//      $('.products-sticky').css('overflow-y','hidden');
// })


//close popup 
$('.close-popup').on('click', function() {
   $('.bg-overlay').css('display', 'none');
    var popupBkg = $('.popup-bkg');
  
    popupBkg.fadeOut(300, function() {
        popupBkg.removeClass('active-flex');
    });
    $('body').removeClass('block-scroll');
  
  //fix overlapping on SAFARI
  setTimeout(function(){        
        $('.products-sticky').css('overflow-y','scroll');
  },500)
})

$('.bg-overlay').on('click', function() {
  $('.bg-overlay').css('display', 'none');
    var popupBkg = $('.popup-bkg');
    popupBkg.fadeOut(300, function() {
        popupBkg.removeClass('active-flex');
    });
    $('body').removeClass('block-scroll');
    setTimeout(function(){        
          $('.products-sticky').css('overflow-y','scroll');
    },500)
})

//button spin for lp
function initButtonSpinner() {
        setTimeout(function() {
            $('.button-spin-lp').each(function() {
                if (!$(this).find("i").length) {
                  $(this).append('<i class="fa fa-circle-o-notch fa-spin fa-2x fa-fw" style="opacity:0"></i>');
                }
                $(this).on('click', function() {
                  $(this).find("i").css('opacity','1');
                  $(this).find("span").hide();
                });
            });
        }, 800);

        window.onpageshow = function() {
            $('.button-spin-lp').find("span").show();
            $('.button-spin-lp').find("i").hide();
        };
}

// customers num on LP counter

function initCustomersCounter(daysLeft) {
  var innitialDays = $('.countdown-progress').data('days-bar');
  var daysDiff = innitialDays - daysLeft;
  var baseNum = Number($('.customers-num').attr('data-num'));
  var step = 27;
  var finalNum = baseNum;
  
  if(daysDiff < 1) {
    $('.customers-num').text(baseNum);
  } else {
    for (var i = 0; i < daysDiff; i++) {
      finalNum += step;
      $('.customers-num').text(finalNum);
    }
  }
}


// END customers num on LP counter

//This is where we call all the module functions
// $(document).ready(function() {
//     if ($('#landing-page .countdown-wrapper').length) {
//         initTimer();
//         setInterval('initTimer()', 1000 );
//     }
//     if ($('#landing-page').length) {
//       setInterval(function(){initCounter()},1000)
//       initButtonSpinner();
//       initCustomersCounter();
//       initStickyATC();
//     }
//   initLpSlider();
// })

